import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import logo from "../icons/bellhoptLogoBlack.svg";
import cartIcon from "../icons/cart.svg";
import burgerMenu from "../icons/mobile-burger-black.svg";
import searchIcon from "../icons/search-icon-black.svg";
import leftArrow from "../icons/leftArrow.svg";

import { getProducts } from "../store/thunks/guestActions";
import {
  setActivePage,
  setBarOff,
  setBarOn,
  setBlurOff,
  setBlurOn,
  updateTerm,
} from "../store/slices/guestSlices";

import SaveCartButton from "./SaveCartButton";

const GuestNav = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const guestBlur = useSelector((state) => state.guestBlur);
  const { cartItems } = useSelector((state) => state.cart);
  const activeCompanyFromStore = useSelector(
    (state) => state.activeCompany.activeCompany
  );

  const [search, setSearch] = useState("");
  const [additionalLogoUrl, setAdditionalLogoUrl] = useState(null);

  // Resolve active company (store or localStorage)
  let companyData = activeCompanyFromStore;
  if (!companyData || !companyData.name) {
    try {
      const stored = localStorage.getItem("activeCompany");
      if (stored) companyData = JSON.parse(stored);
    } catch {
      // ignore
    }
  }

  const companyName = companyData?.name;
  const cartItemCount =
    companyName && cartItems[companyName]
      ? cartItems[companyName].length
      : 0;

  // Cart animation
  const [cartAnimation, setCartAnimation] = useState(false);
  const [prevCartCount, setPrevCartCount] = useState(cartItemCount);

  useEffect(() => {
    if (cartItemCount > prevCartCount) {
      setCartAnimation(true);
      const t = setTimeout(() => setCartAnimation(false), 400);
      return () => clearTimeout(t);
    }
    setPrevCartCount(cartItemCount);
  }, [cartItemCount, prevCartCount]);

  // Add-on mode hides SaveCart
  const isAddOnMode =
    typeof window !== "undefined" &&
    localStorage.getItem("addonMode") === "true";

  // Fetch property-specific logo
  useEffect(() => {
    let mounted = true;
    const propertyRaw = localStorage.getItem("property");
    if (!propertyRaw) return;

    let propertyId = null;
    try {
      propertyId = JSON.parse(propertyRaw)?._id;
    } catch {
      propertyId = null;
    }
    if (!propertyId) return;

    const fetchLogo = async () => {
      try {
        const res = await axios.get(
          `https://api.bellhopt.com/api/v1/logo-by-property/${propertyId}`
        );
        if (mounted && res?.data?.logoUrl) {
          setAdditionalLogoUrl(res.data.logoUrl);
        }
      } catch (err) {
        if (mounted) setAdditionalLogoUrl(null);
        console.error("Error fetching additional logo:", err);
      }
    };

    fetchLogo();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const trimmed = search.trim();
    if (!trimmed) return;

    const searchTerm = encodeURIComponent(trimmed);

    dispatch(updateTerm(trimmed));
    dispatch(getProducts({ term: searchTerm, limit: 48, start: 0 }));
    dispatch(setActivePage({ page: 1 }));

    if (location.pathname !== "/homeproducts") {
      navigate("/homeproducts");
    }

    if (props.func) {
      props.func(10);
    }
  };

  const handleEnterKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const openMobileMenu = () => {
    dispatch(setBarOn());
    dispatch(setBlurOn());
  };

  const closeOverlays = () => {
    dispatch(setBlurOff());
    dispatch(setBarOff());
  };

  const showHamburger =
    location.pathname.startsWith("/guest") ||
    location.pathname === "/homeproducts";

  /* ---------- Liquid Glass Property Logo ---------- */

  const PropertyLogoGlass = ({ url, variant }) => {
    if (!url) return null;

    const isDesktop = variant === "desktop";

    // Increased dimensions for better visibility:
    // mobile: h-[44px] max-w-[170px]
    // desktop: h-[50px] max-w-[210px]
    const heightClass = isDesktop ? "h-[50px]" : "h-[44px]";
    const maxWidthClass = isDesktop
      ? "max-w-[210px]"
      : "max-w-[170px]";
    const borderRadiusClass = isDesktop ? "rounded-[25px]" : "rounded-[22px]";

    return (
      <div
        className={`
          ${heightClass} ${maxWidthClass}
          px-4
          flex items-center justify-center
          ${borderRadiusClass}
          bg-gradient-to-r
            from-white/98
            via-[#F9FAFB]
            to-[#FFF5F4]
          border border-[#E5E7EB]/80
          shadow-[0_4px_14px_rgba(15,23,42,0.10)]
          backdrop-blur-xl
          overflow-hidden
        `}
      >
        <img
          src={url}
          alt="Property logo"
          className="
            max-h-full
            max-w-full
            object-contain
            w-auto
            h-auto
          "
        />
      </div>
    );
  };

  /* ----------------------- RENDER ----------------------- */

  return (
    <>
      {/* Global blur overlay */}
      {guestBlur && (
        <div
          onClick={closeOverlays}
          className="fixed inset-0 z-10 bg-black/40 min-h-[650px]"
        />
      )}

      {/* MOBILE HEADER */}
      <header className="tablet:block laptop:hidden w-full bg-white guest-nav-mobile">
        <div
          className="
            w-full
            px-3 pt-3 pb-4
            flex flex-col gap-3
            fixed top-0 left-0 z-[80]
            bg-white/95
            backdrop-blur-xl
            shadow-sm
          "
        >
          {/* Top row: logos + save cart */}
          <div className="flex items-center justify-between gap-3">
            <NavLink
              to="/guest"
              className="flex items-center gap-3 min-w-0"
            >
              {/* Bellhopt logo - original size */}
              <img
                src={logo}
                alt="Bellhopt"
                className="h-[40px] w-auto"
              />

              {/* Property logo in glass pill, same visual footprint as original */}
              {/* {additionalLogoUrl && (
                <PropertyLogoGlass
                  url={additionalLogoUrl}
                  variant="mobile"
                />
              )} */}
            </NavLink>

            <div className="flex items-center gap-2">
              {!isAddOnMode && <SaveCartButton size="sm" />}

              {/* (Optional) Mobile cart pill currently hidden */}
              <NavLink
                to="/cart"
                className={`
                  hidden
                  flex items-center gap-1
                  px-3 py-1.5
                  rounded-full
                  text-white
                  text-[11px] font-semibold
                  transition-all duration-400 ease-out
                  ${
                    cartAnimation
                      ? "bg-[#FF4D4D] shadow-[0_0_16px_rgba(255,77,77,0.4)] scale-[1.08]"
                      : "bg-[#FF4D4D] shadow-sm"
                  }
                `}
              >
                <img
                  src={cartIcon}
                  alt=""
                  className={`
                    w-[16px] h-[16px]
                    transition-all duration-400 ease-out
                    ${cartAnimation ? "scale-110 -rotate-3" : ""}
                  `}
                />
                <span
                  className={`
                    transition-all duration-400
                    ${cartAnimation ? "font-bold" : ""}
                  `}
                >
                  {cartItemCount}
                </span>
              </NavLink>
            </div>
          </div>

          {/* Second row: burger / back / centered search */}
          <div className="flex items-center gap-1 justify-center">
            {/* Burger */}
            {/* {showHamburger && (
              <button
                onClick={openMobileMenu}
                className="
                  flex items-center justify-center
                  w-9 h-9
                  rounded-full
                  border border-[#E5E7EB]
                  bg-white
                  shadow-sm
                "
                aria-label="Open menu"
              >
                <img
                  src={burgerMenu}
                  alt=""
                  className="w-4 h-4"
                />
              </button>
            )} */}

            {/* Optional back icon */}
            {props.back ? (
              <button
                onClick={() => navigate(-1)}
                className="
                  flex items-center justify-center
                  w-9 h-9
                  rounded-full
                  border border-[#E5E7EB]
                  bg-white
                "
                aria-label="Go back"
              >
                <img
                  src={leftArrow}
                  alt=""
                  className="w-4 h-4"
                />
              </button>
            ) : (
              <div className="w-9 h-9" />
            )}

            {/* Search (centered) */}
            <div className="relative flex-1 flex justify-center">
              <div className="relative w-full max-w-[420px]">
                <span className="absolute inset-y-0 left-3 flex items-center">
                  <img
                    src={searchIcon}
                    alt=""
                    className="w-4 h-4 opacity-80"
                  />
                </span>
                <input
                  type="text"
                  placeholder="Search groceries, drinks, snacks"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleEnterKey}
                  className="
                    w-full h-[40px]
                    rounded-full
                    pl-9 pr-3
                    text-[12px]
                    bg-white/98
                    border border-[#E5E7EB]
                    text-[#111827]
                    outline-none
                    text-center
                    placeholder:text-center placeholder:text-[#9CA3AF]
                  "
                />
              </div>
            </div>
            {/* Right spacer to balance back button and keep search centered */}
            <div className="w-9 h-9" />
          </div>
        </div>

        {/* Spacer so page content isn't hidden behind fixed header */}
        <div className="h-[96px] guest-nav-mobile-spacer" />
      </header>

      {/* DESKTOP HEADER */}
      <header
        className="
          hidden laptop:block
          fixed top-0 left-0
          w-full h-[72px]
          bg-white/92
          backdrop-blur-2xl
          shadow-[0_6px_18px_rgba(15,23,42,0.06)]
          z-[100]
        "
      >
        <div className="w-[90%] mx-auto h-full flex items-center justify-between gap-6">
          {/* Brand + property logo */}
          <NavLink
            to="/guest"
            className="flex items-center gap-4 min-w-0"
          >
            {/* Bellhopt logo - original size */}
            <img
              src={logo}
              alt="Bellhopt"
              className="h-[46px] w-auto"
            />

            {/* Property logo glass pill - original height footprint */}
            {/* {additionalLogoUrl && (
              <PropertyLogoGlass
                url={additionalLogoUrl}
                variant="desktop"
              />
            )} */}
          </NavLink>

          {/* Search */}
          <div className="flex-1 flex justify-center">
            <div className="relative w-full max-w-[520px]">
              <span className="absolute inset-y-0 left-3 flex items-center">
                <img
                  src={searchIcon}
                  alt=""
                  className="w-4 h-4 opacity-75"
                />
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleEnterKey}
                placeholder="Search groceries, drinks, and essentials for your stay"
                className="
                  w-full h-[40px]
                  pl-9 pr-16
                  rounded-full
                  bg-white/98
                  border border-[#E5E7EB]
                  text-[13px]
                  text-[#111827]
                  outline-none
                  placeholder:text-[#9CA3AF]
                "
              />
              <button
                onClick={handleSearch}
                className="
                  absolute inset-y-[6px] right-2
                  px-4
                  rounded-full
                  bg-[#FF4D4D]
                  text-white
                  text-[11px] font-semibold
                  flex items-center justify-center
                  shadow-[0_6px_16px_rgba(248,113,113,0.45)]
                  hover:bg-[#E63F3F]
                  transition-colors
                "
              >
                Search
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {!isAddOnMode && <SaveCartButton />}

            <NavLink
              to="/cart"
              className={`
                flex items-center gap-2
                px-4 py-2
                rounded-full
                text-white
                text-[12px] font-semibold
                transition-all duration-400 ease-out
                ${
                  cartAnimation
                    ? "bg-[#FF4D4D] shadow-[0_0_20px_rgba(255,77,77,0.4)] scale-[1.05]"
                    : "bg-[#FF4D4D] shadow-md"
                }
              `}
            >
              <img
                src={cartIcon}
                alt=""
                className={`
                  w-[18px] h-[18px]
                  transition-all duration-400 ease-out
                  ${cartAnimation ? "scale-110 -rotate-3" : ""}
                `}
              />
              <span
                className={`
                  transition-all duration-400
                  ${cartAnimation ? "font-bold" : ""}
                `}
              >
                {cartItemCount} item
                {cartItemCount !== 1 && "s"}
              </span>
            </NavLink>
          </div>
        </div>
      </header>

      {/* Spacer for desktop so content isn't behind fixed nav */}
      <div className="hidden laptop:block h-[72px]" />
    </>
  );
};

export default GuestNav;
